<template>
  <div class="product-list-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-goods"></i>
              商品管理
            </h1>
            <p class="page-subtitle">管理商品信息、库存、价格和销售状态</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button 
            v-if="selectedProducts.length > 0"
            type="danger" 
            size="medium" 
            @click="handleBatchDelete"
            :loading="batchDeleteLoading"
          >
            <i class="el-icon-delete"></i> 批量删除 ({{ selectedProducts.length }})
          </el-button>
          <el-button type="primary" size="medium" @click="handleCreate">
            <i class="el-icon-plus"></i> 新增商品
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 搜索和筛选区域 -->
      <div class="filter-section">
        <el-card class="filter-card" shadow="hover">
          <div class="filter-form">
            <el-row :gutter="20" align="middle">
              <el-col :span="6">
                <div class="form-item">
                  <label class="form-label">搜索条件</label>
                  <el-input
                    v-model="queryForm.keyword"
                    placeholder="搜索商品名称/编码"
                    clearable
                    size="medium"
                    @keyup.enter.native="handleSearch"
                  >
                    <i slot="prefix" class="el-input__icon el-icon-search"></i>
                  </el-input>
                </div>
              </el-col>
              <el-col :span="4">
                <div class="form-item">
                  <label class="form-label">商品分类</label>
                  <el-select v-model="queryForm.category" placeholder="请选择分类" clearable size="medium" style="width: 100%">
                    <el-option
                      v-for="category in categories"
                      :key="category.category"
                      :label="category.category"
                      :value="category.category"
                    />
                  </el-select>
                </div>
              </el-col>
              <el-col :span="4">
                <div class="form-item">
                  <label class="form-label">商品状态</label>
                  <el-select v-model="queryForm.is_active" placeholder="请选择状态" clearable size="medium" style="width: 100%">
                    <el-option label="上架中" :value="true">
                      <span style="color: #67c23a">
                        <i class="el-icon-success"></i> 上架中
                      </span>
                    </el-option>
                    <el-option label="已下架" :value="false">
                      <span style="color: #f56c6c">
                        <i class="el-icon-error"></i> 已下架
                      </span>
                    </el-option>
                  </el-select>
                </div>
              </el-col>
              <el-col :span="4">
                <div class="form-item">
                  <label class="form-label">推荐商品</label>
                  <el-select v-model="queryForm.is_featured" placeholder="请选择" clearable size="medium" style="width: 100%">
                    <el-option label="推荐商品" :value="true">
                      <span style="color: #409eff">
                        <i class="el-icon-star-on"></i> 推荐商品
                    </span>
                    </el-option>
                    <el-option label="普通商品" :value="false">
                      <span style="color: #909399">
                        <i class="el-icon-star-off"></i> 普通商品
                      </span>
                    </el-option>
                  </el-select>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="form-actions">
                  <el-button type="primary" size="medium" @click="handleSearch" :loading="tableLoading">
                    <i class="el-icon-search"></i> 搜索
                  </el-button>
                  <el-button size="medium" @click="resetSearch">
                    <i class="el-icon-refresh"></i> 重置
                  </el-button>
                  <el-button size="medium" @click="fetchData">
                    <i class="el-icon-refresh"></i> 刷新
                  </el-button>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </div>

      <!-- 数据统计 -->
      <div class="stats-section" v-if="productList.length > 0">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon">
                <i class="el-icon-goods"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ productList.length }}</div>
                <div class="stat-label">商品总数</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon active">
                <i class="el-icon-success"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ productList.filter(p => p.is_active).length }}</div>
                <div class="stat-label">上架中</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon warning">
                <i class="el-icon-circle-close"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ productList.filter(p => !p.is_active).length }}</div>
                <div class="stat-label">已下架</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon featured">
                <i class="el-icon-star-on"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ productList.filter(p => p.is_featured).length }}</div>
                <div class="stat-label">推荐商品</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 商品列表 -->
      <div class="table-section">
        <el-card class="table-card" shadow="hover">
          <div slot="header" class="table-header">
            <span class="table-title">
              <i class="el-icon-document"></i>
              商品列表
            </span>
            <span class="table-count">共 {{ total }} 条记录</span>
          </div>
          
          <el-table
            v-loading="tableLoading"
            :data="productList"
            style="width: 100%"
            @selection-change="handleSelectionChange"
            :header-cell-style="{ background: '#fafbfc', color: '#606266', fontWeight: '600' }"
          >
            <el-table-column type="selection" width="55" align="center" />
            
            <el-table-column label="商品信息" min-width="280">
              <template slot-scope="scope">
                <div class="product-info">
                  <div class="image-container">
                    <img 
                      v-if="!scope.row.imageError && scope.row.image_url"
                      :src="getImageUrl(scope.row.image_url)" 
                      :alt="scope.row.product_name"
                      class="product-image"
                      @load="handleImageLoad(scope.row)"
                      @error="handleImageError(scope.row)"
                    >
                                         <div v-else class="image-placeholder" @click="retryLoadImage(scope.row)" :title="scope.row.image_url ? '点击重试加载' : '暂无图片'">
                       <i class="el-icon-goods"></i>
                       <span>{{ scope.row.image_url ? '加载失败' : '暂无图片' }}</span>
                     </div>
                    <div v-if="scope.row.imageLoading" class="image-loading">
                      <i class="el-icon-loading"></i>
                    </div>
                  </div>
                  <div class="product-details">
                    <div class="product-name">{{ scope.row.product_name }}</div>
                    <div class="product-code">编码: {{ scope.row.product_code }}</div>
                    <div class="product-category">
                      {{ scope.row.category || '未分类' }}
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="零售价格" width="120" align="center">
              <template slot-scope="scope">
                <div class="price-info">
                  <div class="price-value">¥{{ getPrice(scope.row) }}</div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="原价/销量" width="120" align="center">
              <template slot-scope="scope">
                <div class="price-sales-info">
                  <div class="original-price" v-if="scope.row.original_price">
                    <span class="original-price-label">原价:</span>
                    <span class="original-price-value">¥{{ scope.row.original_price }}</span>
                  </div>
                  <div class="sales-quantity">
                    <span class="sales-label">销量:</span>
                    <span class="sales-value">{{ scope.row.sales_quantity || 0 }}</span>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="状态" width="120" align="center">
              <template slot-scope="scope">
                <div class="status-tags">
                  <el-tag :type="getActiveStatus(scope.row) ? 'success' : 'danger'" size="medium">
                    {{ getActiveStatus(scope.row) ? '上架中' : '已下架' }}
                  </el-tag>
                  <el-tag v-if="getFeaturedStatus(scope.row)" type="warning" size="mini">
                    推荐
                  </el-tag>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="320" fixed="right" align="center">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button 
                    size="mini" 
                    icon="el-icon-view" 
                    @click="handleDetail(scope.row)"
                    title="查看详情"
                  >
                    详情
                  </el-button>
                  <el-button 
                    size="mini" 
                    type="primary" 
                    icon="el-icon-edit" 
                    @click="handleEdit(scope.row)"
                    title="编辑商品"
                  >
                    编辑
                  </el-button>
                  <el-button 
                    size="mini" 
                    :type="getActiveStatus(scope.row) ? 'warning' : 'success'"
                    @click="handleToggleStatus(scope.row)"
                  >
                    {{ getActiveStatus(scope.row) ? '下架' : '上架' }}
                  </el-button>
                  <el-button 
                    size="mini" 
                    type="danger" 
                    icon="el-icon-delete"
                    @click="handleDelete(scope.row)"
                    title="删除商品"
                  >
                    删除
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <div class="pagination-section">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="queryForm.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="queryForm.page_size"
              layout="total, sizes, prev, pager, next, jumper"
              :total="total"
              background
            />
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { getAdminProducts, updateProductStatus, getProductCategories, deleteProduct } from '@/api/product'
import { processImageUrl, getUrlWithTimestamp } from '@/utils/config'
import { mapGetters } from 'vuex'

export default {
  name: 'ProductList',
  data() {
    return {
      queryForm: {
        page: 1,
        page_size: 20,
        keyword: '',
        category: '',
        is_active: null,
        is_featured: null
      },
      
      productList: [],
      total: 0,
      tableLoading: false,
      selectedProducts: [],
      categories: [],
      batchDeleteLoading: false
    }
  },
  
  computed: {
    ...mapGetters(['adminInfo']),
    
    // 判断当前用户是否为门店管理员
    isStoreAdmin() {
      return this.adminInfo && this.adminInfo.role_type === 3
    }
  },
  
  created() {
    this.fetchData()
    this.loadCategories()
  },
  

  
  methods: {
    async fetchData() {
      this.tableLoading = true
      try {
        // 统一使用门店商品接口，通过请求头让后端识别管理员身份
        const response = await getAdminProducts(this.queryForm)
        const productList = response.data.list || []
        
        // 处理商品列表，初始化图片状态
        this.productList = productList.map(product => {
          return {
            ...product,
            image_url: product.image_url || product.main_image,
            imageLoading: false,
            imageError: false
          }
        })
        
        this.total = response.data.pagination?.total || 0
      } catch (error) {
        console.error('获取商品列表失败:', error)
        
        // 根据错误类型给出不同的提示
        if (error.message && error.message.includes('门店不存在')) {
          if (this.adminInfo && (this.adminInfo.role_type === 1 || this.adminInfo.role_type === 2)) {
            this.$message.error('系统管理员权限配置异常，请检查后端API权限设置')
          } else {
            this.$message.error('当前用户未关联门店，请联系管理员分配门店权限')
          }
        } else if (error.message && error.message.includes('权限')) {
          this.$message.error('权限不足，请联系管理员检查用户权限配置')
        } else {
          this.$message.error('获取商品列表失败: ' + (error.message || '未知错误'))
        }
      } finally {
        this.tableLoading = false
      }
    },
    
    async loadCategories() {
      try {
        // 统一使用门店分类接口，通过请求头让后端识别管理员身份
        const response = await getProductCategories()
        this.categories = response.data || []
      } catch (error) {
        console.error('获取分类失败:', error)
        // 分类获取失败不影响主要功能，只是失去筛选能力
        this.categories = []
      }
    },
    
    handleSearch() {
      this.queryForm.page = 1
      this.fetchData()
    },
    
    resetSearch() {
      this.queryForm = {
        page: 1,
        page_size: 20,
        keyword: '',
        category: '',
        is_active: null,
        is_featured: null
      }
      this.fetchData()
    },
    
    handleSizeChange(size) {
      this.queryForm.page_size = size
      this.queryForm.page = 1
      this.fetchData()
    },
    
    handleCurrentChange(page) {
      this.queryForm.page = page
      this.fetchData()
    },
    
    handleSelectionChange(selection) {
      this.selectedProducts = selection
    },
    
    handleCreate() {
      this.$router.push('/admin/products/create')
    },
    
    handleDetail(product) {
      this.$router.push(`/admin/products/${product.id}`)
    },
    
    handleEdit(product) {
      this.$router.push(`/admin/products/${product.id}/edit`)
    },
    
    async handleToggleStatus(product) {
      try {
        const currentStatus = this.getActiveStatus(product)
        const newStatus = !currentStatus
        
        await updateProductStatus(product.id, newStatus)
        
        // 更新产品状态（兼容不同字段名）
        if (product.is_active !== undefined) {
          product.is_active = newStatus
        } else if (product.active !== undefined) {
          product.active = newStatus
        } else if (product.status !== undefined) {
          product.status = newStatus ? 1 : 0
        } else if (product.state !== undefined) {
          product.state = newStatus ? 1 : 0
        }
        
        this.$message.success(`商品${newStatus ? '上架' : '下架'}成功`)
      } catch (error) {
        this.$message.error('状态更新失败')
      }
    },
    
    // 获取处理后的图片URL
    getImageUrl(url) {
      return processImageUrl(url)
    },
    
    // 获取商品价格（兼容不同字段名）
    getPrice(product) {
      // 首先检查prices对象中的base_price
      if (product.prices && product.prices.base_price !== undefined) {
        const price = parseFloat(product.prices.base_price) || 0
        return price.toFixed(2)
      }
      
      // 备用：检查prices对象中的其他价格字段
      if (product.prices) {
        const priceValue = product.prices.user_price || 
                          product.prices.retail_price || 
                          product.prices.selling_price ||
                          product.prices.price || 0
        const price = parseFloat(priceValue) || 0
        return price.toFixed(2)
      }
      
      // 最后备用：直接检查商品对象的价格字段
      const directPrice = product.base_price || 
                         product.retail_price || 
                         product.price || 
                         product.selling_price || 0
      const price = parseFloat(directPrice) || 0
      return price.toFixed(2)
    },
    

    

    
    
    // 获取商品上架状态（兼容不同字段名）
    getActiveStatus(product) {
      if (product.is_active !== undefined) return !!product.is_active
      if (product.active !== undefined) return !!product.active
      if (product.status !== undefined) return product.status === 1 || product.status === 'active'
      if (product.state !== undefined) return product.state === 1 || product.state === 'active'
      return false
    },
    
    // 获取推荐状态（兼容不同字段名）
    getFeaturedStatus(product) {
      if (product.is_featured !== undefined) return !!product.is_featured
      if (product.featured !== undefined) return !!product.featured
      if (product.is_recommend !== undefined) return !!product.is_recommend
      if (product.recommend !== undefined) return !!product.recommend
      return false
    },
    
    // 图片加载成功
    handleImageLoad(product) {
      this.$set(product, 'imageLoading', false)
      this.$set(product, 'imageError', false)
    },
    
    // 图片加载失败
    handleImageError(product) {
      this.$set(product, 'imageLoading', false)
      this.$set(product, 'imageError', true)
      console.warn(`图片加载失败: ${product.image_url}`)
    },
    
    // 重试加载图片
    retryLoadImage(product) {
      if (!product.image_url) {
        this.$message.info('该商品暂无图片')
        return
      }
      
      this.$set(product, 'imageLoading', true)
      this.$set(product, 'imageError', false)
      
      // 获取正确的图片URL
      const correctUrl = this.getImageUrl(product.image_url)
      
      // 创建一个新的图片对象来测试加载
      const testImg = new Image()
      testImg.onload = () => {
        this.$set(product, 'imageLoading', false)
        this.$set(product, 'imageError', false)
        console.log('图片重新加载成功:', correctUrl)
        this.$message.success('图片加载成功')
      }
      testImg.onerror = () => {
        this.$set(product, 'imageLoading', false)
        this.$set(product, 'imageError', true)
        console.error('图片加载失败:', correctUrl)
        this.$message.error('图片加载失败，请检查网络连接或图片地址')
      }
      
      // 使用工具函数添加时间戳避免缓存
      testImg.src = getUrlWithTimestamp(correctUrl)
    },
    
    // 删除商品
    handleDelete(product) {
      this.$confirm(
        `确定要删除商品 "${product.product_name}" 吗？`, 
        '删除确认', 
        {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true,
          customClass: 'delete-confirm-dialog',
          message: `
            <div style="margin: 16px 0;">
              <p style="margin-bottom: 12px; color: #303133; font-weight: 600;">商品信息：</p>
              <div style="background: #f5f7fa; padding: 12px; border-radius: 8px; margin-bottom: 12px;">
                <p style="margin: 4px 0; color: #606266;"><strong>商品名称：</strong>${product.product_name}</p>
                <p style="margin: 4px 0; color: #606266;"><strong>商品编码：</strong>${product.product_code}</p>
                <p style="margin: 4px 0; color: #606266;"><strong>价格：</strong>¥${this.getPrice(product)}</p>
                <p style="margin: 4px 0; color: #606266;"><strong>状态：</strong>${this.getActiveStatus(product) ? '上架中' : '已下架'}</p>
              </div>
              <p style="color: #f56c6c; font-weight: 600; margin: 0;">
                <i class="el-icon-warning" style="margin-right: 4px;"></i>
                此操作不可撤销，请谨慎操作！
              </p>
            </div>
          `
        }
      ).then(async () => {
        try {
          const response = await deleteProduct(product.id)
          
          if (response.code === 200) {
            this.$message.success(`商品 "${product.product_name}" 删除成功`)
            
            // 刷新列表
            await this.fetchData()
          } else {
            this.$message.error(response.message || '删除失败')
          }
        } catch (error) {
          console.error('删除商品失败:', error)
          
          // 根据错误类型显示不同提示
          if (error.response && error.response.status === 403) {
            this.$message.error('权限不足，只有系统管理员可以删除商品')
          } else if (error.response && error.response.status === 404) {
            this.$message.error('商品不存在或已被删除')
            // 刷新列表以同步状态
            this.fetchData()
          } else if (error.response && error.response.data && error.response.data.message) {
            this.$message.error(error.response.data.message)
          } else {
            this.$message.error('删除失败，请稍后重试')
          }
        }
             }).catch(() => {
         // 用户取消删除
         this.$message.info('已取消删除')
       })
     },
     
     // 批量删除商品
     handleBatchDelete() {
       if (this.selectedProducts.length === 0) {
         this.$message.warning('请先选择要删除的商品')
         return
       }
       
       const productNames = this.selectedProducts.map(p => p.product_name).join('、')
       const productCount = this.selectedProducts.length
       
       this.$confirm(
         `确定要删除这 ${productCount} 个商品吗？`, 
         '批量删除确认', 
         {
           confirmButtonText: '确定删除',
           cancelButtonText: '取消',
           type: 'warning',
           dangerouslyUseHTMLString: true,
           customClass: 'delete-confirm-dialog',
           message: `
             <div style="margin: 16px 0;">
               <p style="margin-bottom: 12px; color: #303133; font-weight: 600;">即将删除的商品：</p>
               <div style="background: #f5f7fa; padding: 12px; border-radius: 8px; margin-bottom: 12px; max-height: 200px; overflow-y: auto;">
                 ${this.selectedProducts.map(p => `
                   <div style="display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px solid #eee;">
                     <span style="color: #606266; font-weight: 500;">${p.product_name}</span>
                     <span style="color: #909399; font-size: 12px;">¥${this.getPrice(p)}</span>
                   </div>
                 `).join('')}
               </div>
               <p style="color: #f56c6c; font-weight: 600; margin: 0;">
                 <i class="el-icon-warning" style="margin-right: 4px;"></i>
                 此操作不可撤销，将删除 ${productCount} 个商品！
               </p>
             </div>
           `
         }
       ).then(async () => {
         this.batchDeleteLoading = true
         let successCount = 0
         let failCount = 0
         const failedProducts = []
         
         try {
           // 并发删除所有选中的商品
           const deletePromises = this.selectedProducts.map(async (product) => {
             try {
               const response = await deleteProduct(product.id)
               if (response.code === 200) {
                 successCount++
                 return { success: true, product }
               } else {
                 failCount++
                 failedProducts.push(product.product_name)
                 return { success: false, product, error: response.message }
               }
             } catch (error) {
               failCount++
               failedProducts.push(product.product_name)
               return { success: false, product, error: error.message }
             }
           })
           
           await Promise.all(deletePromises)
           
           // 显示结果
           if (successCount > 0 && failCount === 0) {
             this.$message.success(`成功删除 ${successCount} 个商品`)
           } else if (successCount > 0 && failCount > 0) {
             this.$message.warning(`删除完成：成功 ${successCount} 个，失败 ${failCount} 个`)
             console.warn('删除失败的商品:', failedProducts)
           } else {
             this.$message.error(`删除失败：${failedProducts.join('、')}`)
           }
           
           // 清空选择并刷新列表
           this.selectedProducts = []
           await this.fetchData()
           
         } catch (error) {
           console.error('批量删除失败:', error)
           this.$message.error('批量删除操作失败')
         } finally {
           this.batchDeleteLoading = false
         }
       }).catch(() => {
         this.$message.info('已取消批量删除')
       })
     }
   }
 }
</script>

<style scoped>
.product-list-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.header-actions .el-button--danger {
  background: #f56c6c;
  border-color: #f56c6c;
  color: white;
}

.header-actions .el-button--danger:hover {
  background: #f78989;
  border-color: #f78989;
}

/* 主要内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 25px;
}

.filter-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.filter-form {
  padding: 10px 0;
}

.form-item {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  margin-bottom: 8px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
  flex-wrap: wrap;
}

/* 统计卡片 */
.stats-section {
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
}

.stat-icon i {
  color: #4facfe;
  z-index: 1;
}

.stat-icon.active::before {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.active i {
  color: #67c23a;
}

.stat-icon.warning::before {
  background: linear-gradient(135deg, #f56c6c, #f78989);
}

.stat-icon.warning i {
  color: #f56c6c;
}

.stat-icon.featured::before {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.stat-icon.featured i {
  color: #e6a23c;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 表格区域 */
.table-section {
  margin-bottom: 25px;
}

.table-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-count {
  font-size: 14px;
  color: #909399;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding: 30px 0;
}

/* 商品信息样式 */
.product-info {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 8px 0;
}

.image-container {
  position: relative;
  width: 60px;
  height: 60px;
  flex-shrink: 0;
}

.product-image {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid #f0f2f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 2px solid #e4e7ed;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  cursor: pointer;
  transition: all 0.3s ease;
}

.image-placeholder:hover {
  border-color: #409eff;
  color: #409eff;
  background: #ecf5ff;
}

.image-placeholder i {
  font-size: 18px;
  margin-bottom: 2px;
}

.image-placeholder span {
  font-size: 10px;
  line-height: 1;
  white-space: nowrap;
}

.image-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409eff;
  z-index: 10;
}

.image-loading i {
  font-size: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.product-details {
  flex: 1;
}

.product-name {
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
  font-size: 15px;
  line-height: 1.4;
}

.product-code {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
  font-family: monospace;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.product-category {
  font-size: 12px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 4px;
}

.product-category::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #409eff;
}

/* 价格信息样式 */
.price-info {
  text-align: center;
  padding: 8px 0;
}

.price-value {
  font-weight: 600;
  color: #e6a23c;
  font-size: 16px;
  padding: 6px 12px;
  background: #fdf6ec;
  border-radius: 8px;
  border: 1px solid #f5dab1;
  display: inline-block;
}



/* 原价销量信息样式 */
.price-sales-info {
  text-align: center;
  padding: 4px 0;
}

.original-price {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.original-price-label {
  font-size: 11px;
  color: #999;
}

.original-price-value {
  text-decoration: line-through;
  margin-left: 2px;
}

.sales-quantity {
  font-size: 13px;
  color: #606266;
}

.sales-label {
  font-size: 11px;
  color: #999;
}

.sales-value {
  font-weight: bold;
  margin-left: 2px;
}

/* 状态标签样式 */
.status-tags {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 8px 4px;
}

.action-buttons .el-button {
  margin: 0;
  min-width: 50px;
  font-size: 12px;
  padding: 7px 12px;
}

.action-buttons .el-button--danger {
  background: #f56c6c;
  border-color: #f56c6c;
  color: white;
}

.action-buttons .el-button--danger:hover {
  background: #f78989;
  border-color: #f78989;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content {
    padding: 0 20px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .stats-section .el-row {
    display: flex;
    flex-wrap: wrap;
  }
  
  .stats-section .el-col {
    margin-bottom: 15px;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .main-content {
    padding: 0 15px;
  }
  
  .filter-form .el-col {
    margin-bottom: 15px;
  }
  
  .form-actions {
    justify-content: center;
  }
  
  .header-actions .el-button {
    font-size: 14px;
    padding: 8px 16px;
  }
  
  .stats-section .el-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
  
  .stat-card {
    padding: 20px 16px;
    gap: 12px;
  }
  
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 22px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .image-container {
    width: 50px;
    height: 50px;
  }
  
  .image-placeholder i {
    font-size: 14px;
  }
  
  .image-placeholder span {
    font-size: 9px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
    max-width: 80px;
  }
  
  .action-buttons .el-button {
    font-size: 11px;
    padding: 6px 8px;
    min-width: 70px;
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .stats-section .el-col {
    flex: 0 0 100%;
    max-width: 100%;
  }
  
  .product-info {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .product-image {
    width: 50px;
    height: 50px;
  }
  
  .action-buttons {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 3px;
    max-width: none;
  }
  
  .action-buttons .el-button {
    font-size: 10px;
    padding: 4px 6px;
    min-width: 40px;
    flex: 1;
    max-width: 48px;
  }
  
  .action-buttons .el-button span {
    display: none;
  }
  
  .action-buttons .el-button i {
    margin: 0;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .header-actions .el-button {
    font-size: 12px;
    padding: 6px 12px;
    flex: 1;
    max-width: 140px;
  }
}

/* 删除确认对话框样式 */
:deep(.delete-confirm-dialog) {
  border-radius: 12px;
  overflow: hidden;
}

:deep(.delete-confirm-dialog .el-message-box__header) {
  background: linear-gradient(135deg, #f56c6c, #f78989);
  color: white;
  padding: 20px 24px;
  border-bottom: none;
}

:deep(.delete-confirm-dialog .el-message-box__title) {
  color: white;
  font-weight: 600;
  font-size: 16px;
}

:deep(.delete-confirm-dialog .el-message-box__headerbtn) {
  top: 20px;
  right: 20px;
}

:deep(.delete-confirm-dialog .el-message-box__headerbtn .el-message-box__close) {
  color: white;
  font-size: 18px;
}

:deep(.delete-confirm-dialog .el-message-box__content) {
  padding: 24px;
  background: white;
}

:deep(.delete-confirm-dialog .el-message-box__message) {
  margin: 0;
  line-height: 1.5;
}

:deep(.delete-confirm-dialog .el-message-box__btns) {
  padding: 16px 24px 24px;
  background: #f8f9fa;
  border-top: 1px solid #ebeef5;
}

:deep(.delete-confirm-dialog .el-button--primary) {
  background: #f56c6c;
  border-color: #f56c6c;
}

:deep(.delete-confirm-dialog .el-button--primary:hover) {
  background: #f78989;
  border-color: #f78989;
}
</style> 